/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import {
  $SubtenantsFilter,
  GetSubtenantsData,
  GetSubtenantsResponse,
  Subtenant,
  SubtenantsFilter,
  SubtenantsSortableFields,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedSubtenantsQueryParams = {
  params?: Omit<GetSubtenantsData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Custom hook that fetches paginated subtenants with filtering, sorting and real-time updates.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated subtenants response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenantId is automatically pulled from the management plane state store
 */
export function usePaginatedSubtenantsQuery({ params, type }: UsePaginatedSubtenantsQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  return usePaginatedQuery<
    GetSubtenantsResponse,
    Subtenant,
    SubtenantsFilter,
    SubtenantsSortableFields,
    GetSubtenantsData
  >({
    apiCall: mgmtPlaneAPIClient.subtenants.getSubtenants.bind(mgmtPlaneAPIClient.subtenants),
    filterTypes: jsonSchemaToTypeMap($SubtenantsFilter),
    queryKey: 'subtenants',
    realtimeEvents: ['subtenant:created', 'subtenant:updated', 'subtenant:deleted'],
    params: {
      tenantId: selectedTenantId!,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
}
